import React, { useEffect } from "react";
import { Box, Flex, Text } from "components-lib";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { columnsDataConsultation } from "views/admin/default/variables/columnsData";
import ConsultationFilters from "./ConsultationFilters";
import ConsultationStatsView from "./StatsView";
import useSocket from "hooks/useSocket";

/**
 * ConsultationView Component
 * Displays the consultation view with filters, statistics, and consultation table
 * Handles consultation data display and filtering
 * Note: Status and date filtering is now applied at the data level in useConsultationData hook
 */
const ConsultationView = ({
    // User role information
    isOwner,
    isAdmin,
    isDoctor,

    // Doctor selection
    doctors,
    selectedDoctor,
    setSelectedDoctor,
    selectedClinic,

    // Date filtering
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    onClinicSelect,

    // Statistics
    totalAppointments,
    pendingAppointments,
    completedAppointments,

    // Consultation data
    consultationsData,
    filterConsultations,
    filterConsultationsByStatusAndDate,
    patientSearchQuery,

    // Event handlers
    onRowClick,
    // Optional callback parent can provide to receive incoming status updates
    onAppointmentStatusUpdate,
}) => {
    // Shared socket helpers from provider
    const { emit, subscribe } = useSocket();

    // Subscribe to server broadcasts for appointment status updates and forward to parent handler
    useEffect(() => {
        if (typeof subscribe !== "function") return;

        const handler = (payload) => {
            // payload expected: { appointment_id, status, updated_at, type, changed_by }
            if (typeof onAppointmentStatusUpdate === "function") {
                try {
                    onAppointmentStatusUpdate(payload);
                } catch (e) {
                    console.warn("onAppointmentStatusUpdate handler error:", e);
                }
            }
        };

        const cleanup = subscribe("appointment.status.updated", handler);
        return cleanup;
    }, [subscribe, onAppointmentStatusUpdate]);

    // When a row is clicked in consultations, emit IN_PROGRESS via socket and then call parent's onRowClick
    const handleRowClickLocal = (row) => {
        try {
            const apptId = row?.appointment_id ?? row?.id;
            if (emit && apptId) {
                // Emit a status change request; backend should validate and broadcast authoritative update
                emit("appointment.status.change", {
                    appointment_id: apptId,
                    status: "IN_PROGRESS",
                });
            }
        } catch (e) {
            console.warn("Failed to emit IN_PROGRESS via socket:", e);
        }

        // call original row click handler to open consultation UI / do REST update if parent does it
        if (typeof onRowClick === "function") {
            onRowClick(row);
        }
    };
    return (
        <Box width="100%" overflowX="auto" bg="white" borderRadius="lg" p={4}>
            <Flex
                mb="20px"
                bg="white"
                borderRadius="md"
                align="center"
                justify="space-between"
                flexWrap={{ base: "wrap", xl: "nowrap" }}
                gap={4}
            >
                {/* Left side - Filters */}
                <ConsultationFilters
                    isOwner={isOwner}
                    isAdmin={isAdmin}
                    isDoctor={isDoctor}
                    doctors={doctors}
                    selectedDoctor={selectedDoctor}
                    setSelectedDoctor={setSelectedDoctor}
                    selectedClinic={selectedClinic}
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={onFromDateChange}
                    onToDateChange={onToDateChange}
                    onClinicSelect={onClinicSelect}
                />

                {/* Right side - Statistics */}
                {/* <ConsultationStats
          totalAppointments={totalAppointments}
          pendingAppointments={pendingAppointments}
          completedAppointments={completedAppointments}
        /> */}
            </Flex>

            {/* Consultation Table */}
            {consultationsData.length === 0 ? (
                <Text textAlign="center" mt={4}>
                    {selectedDoctor
                        ? "No consultations found for selected doctor"
                        : isDoctor
                          ? "No consultations found for your account"
                          : "Please select a doctor to view consultations"}
                </Text>
            ) : (
                <Box width="100%">
                    <ComplexTable
                        columnsData={columnsDataConsultation}
                        tableData={filterConsultations(
                            consultationsData,
                            patientSearchQuery,
                        )}
                        onAppointmentStatusUpdate={onAppointmentStatusUpdate}
                        // Use local wrapper so we can emit IN_PROGRESS before delegating to parent handler
                        onRowClick={handleRowClickLocal}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ConsultationView;
